import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { isPlatformAdminEmail, planHasFeature } from '@ovation/core'
import { useAuthStore } from './auth-store'
import { artistFetch } from './artist-api'
import type { SessionUser } from './session-types'

export function isPlatformAdmin(user: SessionUser | null | undefined): boolean {
  return !!user && (isPlatformAdminEmail(user.email) || user.accountType === 'internal')
}

/** Org owner/admin or artist-scoped admin — should see full plan features regardless of module toggles. */
export function isTenantAdmin(user: SessionUser | null | undefined): boolean {
  if (!user) return false
  return user.orgRole === 'owner' || user.orgRole === 'admin' || user.role === 'admin'
}

/**
 * Resolve whether a feature is enabled for the current user.
 * Admins bypass per-tenant module toggles and use plan defaults instead.
 */
export function useFeatureChecker(): (feature: string) => boolean {
  const user = useAuthStore((s) => s.user)
  const plan = user?.orgPlan ?? 'management'
  const platformAdmin = isPlatformAdmin(user)
  const tenantAdmin = isTenantAdmin(user)
  const skipToggles = platformAdmin || tenantAdmin

  const { data: toggles } = useQuery<{ resolved: Record<string, boolean> }>({
    queryKey: ['nav-artist-modules', user?.artistId],
    queryFn: () => artistFetch<{ resolved: Record<string, boolean> }>('/modules'),
    enabled: !!user?.artistId && !skipToggles,
    staleTime: 5 * 60_000,
  })

  const effectivePlan = platformAdmin ? 'enterprise' : plan

  return useCallback(
    (feature: string) => {
      if (skipToggles) {
        return planHasFeature(effectivePlan, feature)
      }
      if (toggles?.resolved && feature in toggles.resolved) {
        return toggles.resolved[feature] === true
      }
      return planHasFeature(plan, feature)
    },
    [skipToggles, effectivePlan, toggles?.resolved, plan],
  )
}

export function useFeatureEnabled(feature: string): boolean {
  const check = useFeatureChecker()
  return useMemo(() => check(feature), [check, feature])
}
